import { useState, useEffect, ChangeEvent } from "react";
import {
  Button,
  Card,
  Table,
  Container,
  Row,
  Col,
  Breadcrumb,
  BreadcrumbItem,
  Input,
  InputGroup,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from "reactstrap";
import TenantAPI from "../../api/api/TenantAPI";

import _ from "underscore";
import { Link } from "react-router-dom";
import { AppConfirm } from "../../utils/PopupUtil";
import AppSpinner from "../../elements/AppSpinner";
import AppHelmet from "../../components/AppComponents/AppHelmet";
import AppCopy from "../../components/AppComponents/AppCopy";
import { useQueryClient } from "@tanstack/react-query";
import Constants from "../../utils/Constants";
import { newToastEvent } from "../../utils/ToastUtil";

interface Props {
  params?: {
    tenantID?: string;
  };
}

interface APICredential {
  id?: string;
  _id?: string;
  Name: string;
  APIKey: string;
  viewKey?: boolean;
}

const CRMAPIKeySettingsView = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [apiCredentials, setApiCredentials] = useState<APICredential[]>([]);
  const [addAPIKeyLoader, setAddAPIKeyLoader] = useState<boolean>(false);
  const [changeAPIKeyLoader, setChangeAPIKeyLoader] = useState<boolean>(false);
  const [deleteAPIKeyLoader, setDeleteAPIKeyLoader] = useState<boolean>(false);
  const [changingIndex, setChangingIndex] = useState<number | null>(null);
  const [APIKeyName, setAPIKeyName] = useState<string>("");
  const [addAPIKeyModal, setAddAPIKeyModal] = useState<boolean>(false);

  const queryClient = useQueryClient();

  const toggleModal = (state: "addAPIKeyModal") => {
    if (state === "addAPIKeyModal") {
      setAddAPIKeyModal(!addAPIKeyModal);
    }
  };

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    setAPIKeyName(val);
  };

  const handleAddAPIKey = () => {
    setAddAPIKeyLoader(true);

    TenantAPI.addCredential(APIKeyName)
      .then((data: any) => {
        const temp = [...apiCredentials];
        temp.push(data.data.data.payload);
        setApiCredentials(temp);
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        toggleModal("addAPIKeyModal");
        setAddAPIKeyLoader(false);
        setAPIKeyName("");
        queryClient.invalidateQueries({
          queryKey: [Constants.SUP_CLOUD_ID, "records", "SupportalLog"],
        });
      });
  };

  const handleChangeAPIKey = (id: string, index: number) => {
    AppConfirm({
      title: "Rotate API Key?",
      text: "Are you sure you want to rotate this API key? The existing key will no longer be usable.",
      confirmButtonText: "Rotate",
      cancelButtonText: "Nevermind",
    }).then((result: any) => {
      if (!result.isConfirmed) {
        return;
      }

      setChangeAPIKeyLoader(true);
      setChangingIndex(index);

      TenantAPI.changeAPIKey(id)
        .then((data: any) => {
          const temp = [...apiCredentials];
          temp[index] = data.data.data;
          setApiCredentials(temp);

          AppConfirm({
            title: "API Key Rotated",
            text: "The API Key was successfully rotated. Please copy the new key for future use.",
            icon: "success",
            showCancelButton: false,
            confirmButtonText: "OK",
            confirmButtonColor: "var(--primary)",
          });
          newToastEvent("success", "API Key Rotated");
        })
        .catch((err: any) => {
          console.log(err);
          newToastEvent("error", "Failed to rotate API key.");
        })
        .finally(() => {
          setChangeAPIKeyLoader(false);
          setChangingIndex(null);
          queryClient.invalidateQueries({
            queryKey: [Constants.SUP_CLOUD_ID, "records", "SupportalLog"],
          });
        });
    });
  };

  const handleDeleteAPIKey = (id: string, index: number) => {
    AppConfirm({
      title: "Delete API Key?",
      text: "Are you sure you want to delete this API key? This cannot be undone.",
    }).then((result: any) => {
      if (!result.isConfirmed) return;

      setDeleteAPIKeyLoader(true);
      setChangingIndex(index);

      TenantAPI.deleteCredential(id)
        .then(() => {
          const temp = [...apiCredentials];
          temp.splice(index, 1);
          setApiCredentials(temp);

          AppConfirm({
            title: "API Key Deleted",
            text: "The API Key was successfully deleted.",
            icon: "success",
            showCancelButton: false,
            confirmButtonText: "OK",
            confirmButtonColor: "var(--primary)",
          });
        })
        .catch((err: any) => {
          console.log(err);
        })
        .finally(() => {
          setDeleteAPIKeyLoader(false);
          setChangingIndex(null);
          queryClient.invalidateQueries({
            queryKey: [Constants.SUP_CLOUD_ID, "records", "SupportalLog"],
          });
        });
    });
  };

  const getData = () => {
    setLoading(true);

    TenantAPI.getCredentialsForTenant()
      .then((data: any) => {
        setApiCredentials(data.data.data);
      })
      .catch((err: any) => {
        console.error(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getData();
  }, []);

  return (
    <>
      <AppHelmet title="API Keys - Settings" />
      <div className="header pb-6">
        <Container fluid>
          <div className="header-body">
            <Row className="align-items-center py-4">
              <Col lg="auto" md="12">
                <h6 className="h2 mb-0">API Keys</h6>
                <Breadcrumb
                  className="d-none d-md-block mt-1"
                  listClassName="breadcrumb-links"
                >
                  <BreadcrumbItem>
                    <Link to={`/clouds/${props?.params?.tenantID}`}>
                      <i className="mdi mdi-account-convert" />
                    </Link>
                  </BreadcrumbItem>
                  <BreadcrumbItem>API Keys</BreadcrumbItem>
                </Breadcrumb>
              </Col>
              <Col
                className="text-lg-right mt-3 mt-lg-0"
                lg=""
                md="12"
                style={{
                  overflowY: "scroll",
                  lineHeight: 1,
                  whiteSpace: "nowrap",
                }}
              >
                <Button
                  className="btn-icon btn-outline-primary"
                  color=""
                  onClick={() => {
                    toggleModal("addAPIKeyModal");
                  }}
                >
                  <span className="btn-inner--icon">
                    <i className="mdi mdi-plus" style={{ top: "0px" }} />
                  </span>
                  <span className="btn-inner--text">Add</span>
                </Button>
                <Modal
                  isOpen={addAPIKeyModal}
                  toggle={() => {
                    toggleModal("addAPIKeyModal");
                  }}
                  className="modal-dialog-centered"
                >
                  <ModalHeader
                    toggle={() => {
                      toggleModal("addAPIKeyModal");
                    }}
                  >
                    Add a new API Key
                  </ModalHeader>
                  <ModalBody>
                    <InputGroup>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          width: "100%",
                        }}
                      >
                        <Input
                          placeholder="New Name"
                          type="text"
                          name="APIKeyName"
                          value={APIKeyName}
                          onChange={handleNameChange}
                        />
                      </div>
                    </InputGroup>
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      disabled={addAPIKeyLoader}
                      color="primary"
                      onClick={handleAddAPIKey}
                    >
                      {addAPIKeyLoader ? (
                        <AppSpinner
                          style={{ height: 14, width: 14 }}
                          color="white"
                        />
                      ) : (
                        <>Create</>
                      )}
                    </Button>
                  </ModalFooter>
                </Modal>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
      <Container className="mt--6" fluid>
        <hr className="mt-0 mb-4" />
        <Row>
          <Col xs="12">
            <Card>
              <Table className="align-items-center table-flush" responsive>
                <thead className="thead-light">
                  <tr>
                    <th scope="col">Name</th>
                    <th scope="col">API Key</th>
                    <th scope="col" className="text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="list">
                  {loading ? (
                    <tr>
                      <th scope="row" colSpan={3}>
                        Loading...
                      </th>
                    </tr>
                  ) : apiCredentials && apiCredentials.length > 0 ? (
                    apiCredentials.map((item, i) => (
                      <tr key={i}>
                        <th scope="row">{item.Name}</th>
                        <td className="Name">
                          <Button
                            onClick={() => {
                              const creds = [...apiCredentials];
                              const idx = _.findIndex(creds, { id: item.id });
                              creds[idx].viewKey = !creds[idx].viewKey;
                              setApiCredentials(creds);
                            }}
                            color=""
                            style={{
                              outline: "0",
                              boxShadow: "none",
                            }}
                            className="btn-sm btn-link text-secondary mr-2 pl-0"
                          >
                            {item.viewKey ? (
                              <i className="mdi mdi-eye-off"></i>
                            ) : (
                              <i className="mdi mdi-eye"></i>
                            )}
                          </Button>
                          {item.viewKey ? (
                            <>
                              <pre className="d-inline mr-2">{item.APIKey}</pre>
                              <AppCopy text={item.APIKey} />
                            </>
                          ) : (
                            "***-********-****-****-****-************"
                          )}
                        </td>
                        <td className="text-right">
                          <Button
                            size="sm"
                            color=""
                            className="btn-outline-secondary"
                            disabled={changeAPIKeyLoader && changingIndex === i}
                            onClick={() => {
                              handleChangeAPIKey(item.id || item._id || "", i);
                            }}
                          >
                            {changeAPIKeyLoader && changingIndex === i ? (
                              <AppSpinner style={{ height: 14, width: 14 }} />
                            ) : (
                              <>Rotate API Key</>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            color=""
                            className="btn-outline-danger"
                            disabled={deleteAPIKeyLoader && changingIndex === i}
                            onClick={() => {
                              handleDeleteAPIKey(item.id || item._id || "", i);
                            }}
                          >
                            {deleteAPIKeyLoader && changingIndex === i ? (
                              <AppSpinner style={{ height: 14, width: 14 }} />
                            ) : (
                              <div>
                                <span className="btn-inner--icon">
                                  <i
                                    className="mdi mdi-delete mr-2"
                                    style={{ top: "0px" }}
                                  />
                                </span>
                                <span className="btn-inner--text">Delete</span>
                              </div>
                            )}
                          </Button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={3}>None.</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default CRMAPIKeySettingsView;
